// @flow
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import classNames from '@ta/common.classnames';
import { RouteContext } from '@ta/common.routing';
import { localize } from '@ta/common.i18n';
import { Device } from '@ta/lithium.context';
import { useWindowScroll } from '@ta/events.window-scroll';
import * as Import from '@ta/platform.import';
import { featureIsEnabled } from '@ta/common.features';
import { NavTabs } from '@ta/styleguide.nav-tabs';
import GlobalNavContext from '@ta/brand.global-nav-context';
import { QuickLinksLite } from '@ta/brand.quick-links';
import GeoPill from '@ta/brand.global-nav-geopill';
import { GeoPillSponsor } from '@ta/brand.sponsored-geopill';
import Typeahead, { getTypeaheadScopeFromRoute } from '@ta/search.typeahead';
import SideBar from './SideBar';
import NavLinks from './GlobalNavLinks';
import GlobalNavLinksV2 from './GlobalNavLinksV2';
import type { NavLinksDisplayType } from './GlobalNav';
import Logo from './Logo';
import GlobalNavActions from './GlobalNavActions';
import T4BNav, { t4bNavPages } from './T4BNav';
import styles from './GlobalNavBar.less';

const SatelliteGeoPill = Import.component.onUsage(() =>
  import('@ta/brand.global-nav-satellite-geopill'),
);

/**
 * Scroll positions at which different elements of the global nav appear. The geo pill and quick
 * links are visible above the fold, so they don't need to be rendered in the sticky nav bar until
 * after a certain point.
 *
 * These pixel values don't necessarily reflect the exact point at which something is off screen,
 * since the page layout could change in the future. They're safe estimates so we can avoid ever
 * not showing quick links.
 */
const quickLinksScrollOffsetMobile = 260;
const quickLinksScrollOffsetDesktop = 300;
const geoPillScrollOffsetMobile = 84;
const geoPillScrollOffsetDesktop = 150;

const noGeoPillPages = [
  'HomeRebranded',
  'UserReviewEdit',
  'RentalCars',
  'FlightSearchResults',
  'CheapFlightsSearchResults',
  'MobileFlightSearchResults',
  'AirportDetail',
  'MobileAirportDetail',
  'MobileFlights',
  'MobileAirlineDetail',
  'SearchForums',
  'UserListDetail',
  'Travel',
  'AttractionProductDetail',
  'MobileAttractionProductDetail',
  'MobileAccommodationReviewSearch',
  'MobileFilters',
  'MobileAccommodationDetail',
  'MobileVacationRentalDetail',
  'MobileAttractionDetail',
  'MobileHotel_Review',
  'MobileShowUserReviews',
  'MobileShowForum',
  'MobileShowTopic',
  'MobileNewTopic',
  'MobileShowSpecialOffer',
  'MobileLocationPhotoDirectLink',
  'MobileReservation',
  'MobileRoadTrip',
  'MobileRoadTripGuide',
  'MobileGuideOverview',
  'MobileShowAnswersList',
  'MobileEateryReviewSearch',
  'MobileGuideDetail',
  'MobileRestaurantMenu',
  'MobileUserReview',
  'MobileWebUpload',
  'MobileQueryBBox',
  'MobileOpeningHours',
  'MobileAttractionReviewSearch',
  'ShoppingCoupon',
  'Search',
  'RoadTrip',
  'RoadTripGuide',
  'Vacation_Packages',
  'MobileVacation_Packages',
  ...t4bNavPages,
];

const geoPillTabletAndAbovePages = [
  'Hotel_Review',
  'VacationRentalReview',
  'Restaurant_Review',
  'Attraction_Review',
  'AttractionOperatorReview',
];

const noSearchPages = ['Search', 'MobileFlightSearchResults', ...t4bNavPages];

const searchTabletAndAbovePages = ['FlightSearchResults'];

type Props = {|
  servletName: string,

  /** Display type for nav links (default, quick links lite, T4B, or none) */
  navLinksDisplayType: NavLinksDisplayType,

  /** Certain browser are obsolete and need different styling (I.E. <= 10.0) */
  isObsoleteBrowser: boolean,

  /** Height of the global nav */
  navHeight: number,

  /** Returns true if the header should display a geopill */
  hasGeopill: boolean,

  /** Whether to show nav links at all (we don't when nav is in 2-line display mode) */
  displayMultiLine: boolean,

  /** Whether we show a very simple header with just the logo and search icon. */
  liteHeader: boolean,

  /**
   * Whether we're showing the sticky header. Need this info on mobile because if true we don't do the dynamic icon moving
   * that we do on mobile hotels and other places that there is a logo on top of a geopill.
   */
  isStickyHeader: boolean,
|};

/** The component that colors the background of the nav bar and holds the children (logo, hamburger, ...) */
const GlobalNavBar = ({
  servletName,
  navLinksDisplayType,
  isObsoleteBrowser,
  navHeight,
  hasGeopill,
  displayMultiLine,
  liteHeader,
  isStickyHeader,
}: Props) => {
  const { usePersistentIcons, numLines } = useContext(GlobalNavContext);
  const { route } = useContext(RouteContext);
  const { viewportCategory } = useContext(Device);
  const [iconTop, setIconTop] = useState('0px');
  const [iconBottom, setIconBottom] = useState('auto');
  const [showQuickLinks, setShowQuickLinks] = useState(false);
  const [showGeoPill, setShowGeoPill] = useState(false);
  const [isScrolledPastSearch, setIsScrolledPastSearch] = useState(false);

  /** We keep track of whether the geo pill is open in order to address an issue with Safari. On that browser
   * specially, we have a workaround (see useBlockScroll.jsx) that ends up scrolling the page to the top
   * when the geo pill is opened. Therefore, if the geopill is opened, we should not try to calculate
   * the floating quicklinks and geopill's visibility.
   * */
  const [isGeoPillOpen, setIsGeoPillOpen] = useState(false);

  const sideBarTriggerRef = useRef<?HTMLElement>();

  const isMobile = viewportCategory === 'MOBILE';

  const wrapLogoWithH1 =
    (route.page === 'Home' || route.page === 'Tourism' || route.page === 'MobileTourism') &&
    (!+route.geo ||
      +route.geo === 1 ||
      !featureIsEnabled('breadcrumbs_and_masthead_h1_on_new_tourism'));

  const isHome =
    servletName === 'Home' || servletName === 'Tourism' || servletName === 'MobileTourism';

  // True if we're not showing the geopill due to the single search test
  const geoPillSupressed =
    featureIsEnabled('cx_single_search_variant1') ||
    featureIsEnabled('cx_single_search_variant2') ||
    // Replaces the global nav search and hero, but only on Home/Tourism
    (featureIsEnabled('cx_single_search_home') && isHome) ||
    // Just suppresses the geopill on pages other than Home/Tourism
    (featureIsEnabled('cx_single_search_suppress_geopill') && !isHome);

  /** Whether there will be a satellite geopill shown after scrolling past the trip search geopill area. */
  const hasSatelliteGeoPill =
    route.page === 'Home' || route.page === 'Tourism' || route.page === 'MobileTourism';

  /** Extra predicates to determine if the geopill should show. False hides the geopill. */
  const extraGeoPillPredicates =
    !noGeoPillPages.includes(route.page) &&
    !(geoPillTabletAndAbovePages.includes(route.page) && isMobile) &&
    !geoPillSupressed;

  const extraSearchPredicates =
    !noSearchPages.includes(route.page) &&
    !(searchTabletAndAbovePages.includes(route.page) && isMobile);

  /**
   * True if we should temporarily hide the single search based to the current scroll position.
   * This is true in test variants where a search box is shown above the fold, so we don't want
   * a duplicate one in the global nav.
   */
  const hideSingleSearchOnScroll =
    (featureIsEnabled('cx_single_search_variant1') ||
      featureIsEnabled('cx_single_search_variant2') ||
      featureIsEnabled('cx_single_search_home')) &&
    extraSearchPredicates &&
    servletName === 'Home' &&
    !route.geo &&
    !route.isNearby;

  /**
   * Show quicklinks regardless of the scroll position. Single search variant 2 removes the regular
   * desktop quick links, so always show the lite quick links in the global nav regarless of scroll
   * position.
   */
  let showQuicklinksOverride = showQuickLinks;
  if (featureIsEnabled('cx_single_search_variant2')) {
    showQuicklinksOverride = !isMobile;
  }

  const willRenderNavLinksMultiLine =
    displayMultiLine &&
    (navLinksDisplayType === 'DEFAULT' || navLinksDisplayType === 'DEFAULT_WITH_SEO_LINKS');

  const willRenderQuickLinksMultiLine =
    displayMultiLine && navLinksDisplayType === 'QUICK_LINKS_LITE' && showQuicklinksOverride;

  const secondLineNavLinkClasses = classNames(styles.secondLineNavLinks, styles.navLinksWrapper, {
    [styles.isQuickLinks]: navLinksDisplayType === 'QUICK_LINKS_LITE',
  });

  /**
   * This function will determine where the header icons (sideBar icon, nav actions) should be positioned, as well as
   * update the state for the quick links
   */
  const checkScroll = useCallback(() => {
    /* eslint-disable-next-line no-restricted-globals */
    const { pageYOffset } = window;

    const iconsCanMove = usePersistentIcons && !isStickyHeader;
    if (iconsCanMove) {
      const height = navHeight; // The height of the header
      const iconsHeight = sideBarTriggerRef.current // The height of the hamburger menu
        ? sideBarTriggerRef.current.clientHeight
        : height / 2;
      const iconDistanceToTop = height - iconsHeight; // When the icons are stuck to the bottom of the header, this value is their distance to the top of the window.

      /* eslint-disable no-restricted-globals */
      const offsetPosition = window.pageYOffset;
      const visibleHeaderHeight = height - offsetPosition;

      if (offsetPosition <= 0) {
        // If we are scrolled to the very top of the page, set the icons to be at the top of the header
        setIconTop('0px');
        setIconBottom('auto');
      } else if (
        visibleHeaderHeight > 0 &&
        offsetPosition > 0 &&
        offsetPosition <= iconDistanceToTop
      ) {
        // Else if we have scrolled less than than halfway down the header, stick the icons to the top of the header
        setIconTop(`${offsetPosition}px`);
        setIconBottom('auto');
      } else {
        // Otherwise, we have scrolled past the header and should set the icons to be at the bottom of the header
        setIconTop('auto');
        setIconBottom('0px');
      }
    }

    // If quick links are enabled, and the page is showing persistent header, show or hide them when the viewport is scrolled past a certain
    // point
    if (
      !isGeoPillOpen &&
      displayMultiLine &&
      navLinksDisplayType === 'QUICK_LINKS_LITE' &&
      featureIsEnabled('persistent_header_unscoped_bu_home')
    ) {
      const quickLinksOffset = isMobile
        ? quickLinksScrollOffsetMobile
        : quickLinksScrollOffsetDesktop;
      setShowQuickLinks(pageYOffset > quickLinksOffset);
    }

    // If the geopill is enabled, and the page is showing persistent header, show or hide it when the viewport is scrolled past a certain point
    if (
      !isGeoPillOpen &&
      featureIsEnabled('global_nav_geopill') &&
      hasGeopill &&
      extraGeoPillPredicates &&
      hasSatelliteGeoPill &&
      featureIsEnabled('persistent_header_unscoped_bu_home')
    ) {
      const geoPillOffset = isMobile ? geoPillScrollOffsetMobile : geoPillScrollOffsetDesktop;
      setShowGeoPill(pageYOffset > geoPillOffset);
    }

    // If single search is enabled and we're on home, show it when we're scrolled down
    if (hideSingleSearchOnScroll) {
      const searchOffset = 215 - navHeight;
      setIsScrolledPastSearch(pageYOffset >= searchOffset);
    }
  }, [
    extraGeoPillPredicates,
    hasGeopill,
    hasSatelliteGeoPill,
    isMobile,
    isStickyHeader,
    navHeight,
    navLinksDisplayType,
    displayMultiLine,
    usePersistentIcons,
    isGeoPillOpen,
    hideSingleSearchOnScroll,
  ]);

  useWindowScroll(checkScroll, { throttle: 0 });
  useEffect(checkScroll, [checkScroll]);

  const onGeoPillOpened = useCallback(() => {
    setIsGeoPillOpen(true);
  }, [setIsGeoPillOpen]);

  const onGeoPillClosed = useCallback(() => {
    setIsGeoPillOpen(false);
  }, [setIsGeoPillOpen]);

  // The styling for the divs containing sideBar trigger (hamburger menu) and the global nav actions.
  const headerIconsStyle = usePersistentIcons ? { top: iconTop, bottom: iconBottom } : null;

  const singleSearchHidden = hideSingleSearchOnScroll && !isScrolledPastSearch;

  return (
    <GeoPillSponsor adAreaId="globalnav">
      {sponsorDataProvider => {
        let geoPill;
        if (hasGeopill && featureIsEnabled('global_nav_geopill') && extraGeoPillPredicates) {
          if (hasSatelliteGeoPill && showGeoPill) {
            geoPill = (
              <div className={styles.satelliteGeoPillWrap}>
                <SatelliteGeoPill
                  sponsorDataProvider={sponsorDataProvider}
                  onOpen={onGeoPillOpened}
                  onClose={onGeoPillClosed}
                />
              </div>
            );
          } else if (!hasSatelliteGeoPill) {
            geoPill = <GeoPill sponsorDataProvider={sponsorDataProvider} />;
          }
        }

        return (
          <>
            <div className={styles.globalNavBar}>
              <div
                className={classNames('ui_container', styles.globalNavBarContainer, {
                  [styles.obsoleteBrowser]: isObsoleteBrowser,
                  [styles.globalNavMobileWebRollout]: !isObsoleteBrowser,
                  [styles.adjustHeightForTablet]:
                    !isObsoleteBrowser && numLines === 1 && navLinksDisplayType === 'DEFAULT',
                  [styles.persistentIcons]: !isObsoleteBrowser && usePersistentIcons,
                })}
              >
                {navLinksDisplayType === 'T4B' ? (
                  <T4BNav isMobileNav />
                ) : (
                  isMobile && (
                    <div
                      ref={sideBarTriggerRef}
                      style={headerIconsStyle}
                      className={classNames(styles.sideBarTrigger, {
                        [styles.persistentIcons]: usePersistentIcons,
                      })}
                    >
                      <SideBar />
                      <NavLinks sourceOnly />
                    </div>
                  )
                )}

                {/* If the satellite geopill is showing and we're on mobile, we visually replace the logo with the geopill */}
                {!(showGeoPill && isMobile) &&
                  (wrapLogoWithH1 ? (
                    <h1>
                      <Logo />
                    </h1>
                  ) : (
                    <Logo />
                  ))}

                {geoPill}

                {!displayMultiLine && !isMobile && navLinksDisplayType === 'DEFAULT' && (
                  <div className={`is-hidden-mobile ${styles.singleLineNavLinks}`}>
                    <NavLinks />
                  </div>
                )}
                <div
                  style={headerIconsStyle}
                  className={classNames(styles.globalNavActions, {
                    [styles.persistentIcons]: usePersistentIcons,
                    [styles.singleSearchHidden]: singleSearchHidden,
                  })}
                >
                  <GlobalNavActions liteHeader={liteHeader} />
                </div>

                {extraSearchPredicates && (
                  <div
                    className={classNames(styles.singleSearchWrapper, {
                      [styles.singleSearchHidden]: singleSearchHidden,
                    })}
                  >
                    <Typeahead
                      source="SINGLE_SEARCH_NAV"
                      scope={getTypeaheadScopeFromRoute(route)}
                      placeholderText={localize('common_Search') /* Search */}
                      color="gray"
                      hidden={singleSearchHidden && isMobile}
                      styleVariant="expanding"
                    />
                  </div>
                )}
              </div>
            </div>

            {navLinksDisplayType === 'T4B' ? (
              <T4BNav />
            ) : (
              ((!isMobile && willRenderNavLinksMultiLine) || willRenderQuickLinksMultiLine) && (
                <NavTabs>
                  <div className={secondLineNavLinkClasses}>
                    <div className={`ui_container ${styles.secondLineContainer}`}>
                      {willRenderNavLinksMultiLine &&
                        (featureIsEnabled('global_nav_links_test') ? (
                          <GlobalNavLinksV2 />
                        ) : (
                          <NavLinks />
                        ))}
                      {willRenderQuickLinksMultiLine && (
                        <QuickLinksLite
                          color="black"
                          displayMode={isMobile ? 'ICONS' : 'TEXT'}
                          sponsorDataProvider={sponsorDataProvider}
                          onGeoPillOpen={onGeoPillOpened}
                          onGeoPillClose={onGeoPillClosed}
                        />
                      )}
                    </div>
                  </div>
                </NavTabs>
              )
            )}
          </>
        );
      }}
    </GeoPillSponsor>
  );
};

export default GlobalNavBar;
